import { Component, OnInit, Input } from '@angular/core';
import { MatIconRegistry, MatIcon } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { DirectDebitDialogComponent } from 'src/app/direct-debit-dialog/direct-debit-dialog.component';
import { AccountService } from 'src/app/core/services/account.service';
import { GooleAnalyticsEventsService } from 'src/app/core/services/goole-analytics-events.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { BannerNotificationService } from 'src/app/core/services/banner-notification.service';
import { MatDialog } from '@angular/material/dialog';
import { BannerNotificationComponent } from 'src/app/banner-notification/banner-notification.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { environment } from 'src/environments/environment';
import { EnrichmentThemesDialogComponent } from 'src/app/enrichment-themes-dialog/enrichment-themes-dialog.component';
import { UploadVaccinationComponent } from 'src/app/banner-notification/upload-vaccination/upload-vaccination.component';


@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: true,
  imports: [MatIcon]
})
export class PageHeaderComponent implements OnInit {
  @Input() extraClass: string;
  @Input() headerImage: string;
  @Input() imageSrc: string;
  @Input() imageAlt: string;
  @Input() imageLink: string;
  @Input() hideDebitStatus: boolean;
  directDebitEnabled = true;
  directDebitLink = '';
  userActive: boolean;
  showNotifications: boolean;
  notifications = [];
  characters = 150;
  themeSeasonalImageUrl = null;
  themeName = null;
  themeDescription = null;
  showComplianceBanner: boolean = false;
  complianceBannerLabel: string = "We're missing some required details — update now";
  vaccinationDialogData: { vaccinationDue: any, message: string, missingDetails: any } = null;

  constructor(
    private bannerService: BannerNotificationService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private accountService: AccountService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private googleAnalyticsEvents: GooleAnalyticsEventsService,
    private authService: AuthService) {
    this.userActive = localStorage.getItem('active') === 'true';
    this.matIconRegistry.addSvgIcon(
      'exclamation_icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl(environment.base + 'assets/images/icons/exclamation_icon.svg')
    );
    breakpointObserver.observe([
      Breakpoints.Handset,
    ]).subscribe(result => {
      if (result.matches) {
        this.characters = 30
      }
    });
    breakpointObserver.observe([
      Breakpoints.TabletPortrait
    ]).subscribe(result => {
      if (result.matches) {
        this.characters = 75
      }
    });
  }

  ngOnInit() {
    if (this.userActive) {
      if (!this.hideDebitStatus) {
        this.fetchVaccinationReminders().then(wasVaccinationShown => {
          if (!wasVaccinationShown) {
            this.fetchAccountInfo();
            this.fetchCurrentEnrichmentTheme();
          }
        });
        this.fetchBannerNotifications();
      } else {
        return;
      }
    }
  }

  fetchAccountInfo() {
    this.accountService.getDirectDebitLinks().subscribe(resp => {
      if (resp) {
        const currentDate = new Date();
        const response = resp['response'];
        this.directDebitEnabled = response['enabled'];
        this.directDebitLink = response['direct_debit_link'];

        // Display direct debit reminder dialog if needed
        if (!this.directDebitEnabled) {
          const directDebitDateReminderString: string | null = localStorage.directDebitReminderDate;

          if (directDebitDateReminderString == null) {
            this.setLocalStorageValues();
            const dialogRef = this.dialog.open(DirectDebitDialogComponent);
            dialogRef.componentInstance.setUpDirectDebit.subscribe(result => {
              if (result === true) {
                if (this.directDebitLink) {
                  window.open(this.directDebitLink, '_self');
                }
              }
            });
          } else {

            const reminderDate = new Date(directDebitDateReminderString);

            if (reminderDate < currentDate) {
              this.setLocalStorageValues();
              const dialogRef = this.dialog.open(DirectDebitDialogComponent);
              dialogRef.componentInstance.setUpDirectDebit.subscribe(result => {
                if (result === true) {
                  if (this.directDebitLink) {
                    window.open(this.directDebitLink, '_self');
                  }
                }
              });
            }
          }
        }
      }

    }, err => {
      console.error('GET DIRECT DEBIT STATUS FETCH ERROR! ', err);
    });
  }

  fetchCurrentEnrichmentTheme() {
    this.accountService.getCurrentEnrichmentTheme().subscribe(resp => {
      if (resp) {
        const currentDate = new Date();
        const response = resp['theme_for_week'];
        this.themeSeasonalImageUrl = response['seasonal_image_url'];
        this.themeName = response['theme_name'];
        this.themeDescription = response['theme_sentence'];

        // Display enrichment reminder dialog if needed
        if (this.themeName) {
          const enrichmentThemeReminderString: string | null = localStorage.enrichmentThemeReminderDate;

          if (!enrichmentThemeReminderString) {
            this.setThemesLocalStorageValues();
            const dialogRef = this.dialog.open(EnrichmentThemesDialogComponent, {
              data: {
                image: this.themeSeasonalImageUrl,
                dogNames: this.themeDescription['names'],
                subHeader: this.themeDescription['sub_header'],
                themeName: this.themeName,
              },
              disableClose: true,
            });

            setTimeout(() => {
              dialogRef.close();
            }, 5000);
          } else {
            const reminderDate = new Date(enrichmentThemeReminderString);
            if (reminderDate < currentDate) {
              this.setThemesLocalStorageValues();
              const dialogRef = this.dialog.open(EnrichmentThemesDialogComponent, {
                data: {
                  image: this.themeSeasonalImageUrl,
                  dogNames: this.themeDescription['names'],
                  subHeader: this.themeDescription['sub_header'],
                  themeName: this.themeName,
                },
                disableClose: true,
              });

              setTimeout(() => {
                dialogRef.close();
              }, 5000);
            }
          }
        }
      }
    }, err => {
      console.error('GET CURRENT ENRICHMENT THEME FETCH ERROR!', err);
    });
  }

  setThemesLocalStorageValues(): void {
    const currentDate = new Date();
    const reminderDate = new Date(currentDate.getTime() + 45 * 60 * 1000); // 45 minutes in ms
    localStorage.enrichmentThemeReminderDate = reminderDate.toISOString();
  }

  fetchBannerNotifications() {
    this.bannerService.getBannerNotifications().subscribe(res => {
      if (res) {
        this.notifications = res['banner_notifications'];

        if (this.notifications) {
          this.showNotifications = true;
        }
      }
    }, err => {
      console.error('GET BANNER NOTIFICATIONS STATUS FETCH ERROR! ', err);
    });
  }

  openDialog(notification: { message: string, id: number, vax: boolean }) {
    let dialogRef = this.dialog.open(BannerNotificationComponent, {
      data: { body: notification }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == 'read') {
        this.bannerService.sendReadBannerNotification(notification.id).subscribe(res => {
          this.notifications = this.notifications.filter((banner) => banner.id != notification.id)
        })
      }
    })
  }

  navigateTo(path: string) {
    if (this.directDebitLink) {
      this.googleEvent('banner', 'enable direct debit');
      window.open(this.directDebitLink, '_self');
    }
  }

  googleEvent(section, cardTitle) {
    this
      .googleAnalyticsEvents
      .eventEmitter(cardTitle, 'page top - ' + section, 'direct debit', 10);
  }

  setLocalStorageValues(): void {
    const currentDate = new Date();
    const reminderDate = currentDate.setDate(currentDate.getDate() + 11);
    localStorage.directDebitReminderDate = reminderDate;
  }

  hasMissingDetails(missingDetails: { alt_contact?: boolean, missing_alt_contact_name?: boolean, missing_alt_contact_number?: boolean, dogs?: any[] }): boolean {
    if (!missingDetails) {
      return false;
    }
    return !!missingDetails.missing_alt_contact_name
      || !!missingDetails.missing_alt_contact_number
      || !!(missingDetails.dogs && missingDetails.dogs.length > 0);
  }

  fetchVaccinationReminders(): Promise<boolean> {
    return new Promise((resolve) => {
      this.accountService.getCurrentVaccinationReminders().subscribe(resp => {
        if (resp) {
          const vaccinationDue = resp['vaccination_due'];
          const vaccinationMessage = resp['message'];
          const missingDetails = resp['missing_details'];
          const missingComplianceDetails = this.hasMissingDetails(missingDetails);

          if (vaccinationDue || missingComplianceDetails) {
            this.vaccinationDialogData = {
              vaccinationDue: vaccinationDue,
              message: vaccinationMessage,
              missingDetails: missingDetails,
            };
            // Vaccination-due already has its own visibility: the admin-managed banner
            // (banner_notifications with vax: true) and this modal's own auto-popup.
            // This persistent bar exists only for missing compliance details, which has
            // no other indicator once the auto-popup's 24h cooldown hides the modal.
            this.showComplianceBanner = missingComplianceDetails;

            if (this.shouldAutoOpenVaccinationModal()) {
              this.setVaccinationLocalStorageValues();
              this.openVaccinationModal();
              resolve(true);
            } else {
              resolve(false);
            }
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      }, err => {
        console.error('VACCINATION REMINDER FETCH ERROR!', err);
        resolve(false);
      });
    });
  }

  shouldAutoOpenVaccinationModal(): boolean {
    const vaccinationReminderString: string | null = localStorage.vaccinationReminderDate;
    if (!vaccinationReminderString) {
      return true;
    }
    return new Date(vaccinationReminderString) < new Date();
  }

  openVaccinationModal(): void {
    this.dialog.open(UploadVaccinationComponent, { data: this.vaccinationDialogData });
  }

  setVaccinationLocalStorageValues(): void {
    const currentDate = new Date();
    const reminderDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000); // 24 hours in ms
    localStorage.vaccinationReminderDate = reminderDate.toISOString();
  }

}
